import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signUp, confirmSignUp, signIn, resendCode } from "../lib/auth";
import { Logo } from "../components/Logo";
import { AigenticoBadge } from "../components/AigenticoBadge";

type Phase = "signup" | "confirm";

export function SignUp() {
  const nav = useNavigate();
  const [phase, setPhase] = useState<Phase>("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSignUp(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await signUp(email, password);
      setPhase("confirm");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const r = await confirmSignUp(email, code);
      if (r.isComplete) {
        await signIn(email, password);
        nav("/app", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setError("");
    try {
      await resendCode(email);
      setError("A new code was sent.");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 py-6">
        <div className="flex items-center justify-between gap-4">
          <Link to="/"><Logo /></Link>
          <a href="https://aigentico.ai" target="_blank" rel="noopener" title="An Aigentico product — visit Aigentico" className="shrink-0">
            <AigenticoBadge size={32} />
          </a>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        {phase === "signup" ? (
          <form onSubmit={onSignUp} className="w-full max-w-md space-y-6">
            <div>
              <h2 className="mb-2">Create an account</h2>
              <p className="text-ink-soft text-sm">
                Already have one?{" "}
                <Link to="/signin" className="text-aigentico-navy font-medium hover:underline">Sign in.</Link>
              </p>
            </div>

            <div>
              <label className="label" htmlFor="email">Email</label>
              <input id="email" type="email" autoComplete="email" required className="input"
                value={email} onChange={e => setEmail(e.target.value)} />
            </div>

            <div>
              <label className="label" htmlFor="password">Password</label>
              <input id="password" type="password" autoComplete="new-password" required className="input"
                value={password} onChange={e => setPassword(e.target.value)} />
              <p className="text-xs text-ink-faint mt-2">
                Minimum 12 characters. Must contain upper and lower case letters and a digit.
              </p>
            </div>

            {error && <div className="text-aigentico-red text-sm border border-aigentico-red bg-white px-4 py-3 rounded-md">{error}</div>}

            <button type="submit" disabled={busy} className="btn-primary w-full">
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>
        ) : (
          <form onSubmit={onConfirm} className="w-full max-w-md space-y-6">
            <div>
              <h2 className="mb-2">Confirm your email</h2>
              <p className="text-ink-soft text-sm">
                We sent a 6-digit code to <span className="text-ink font-medium">{email}</span>.
              </p>
            </div>

            <div>
              <label className="label" htmlFor="code">Verification code</label>
              <input id="code" required autoComplete="one-time-code" inputMode="numeric"
                className="input font-mono tracking-widest text-lg"
                value={code} onChange={e => setCode(e.target.value)} maxLength={6} />
            </div>

            {error && <div className="text-ink-soft text-sm border border-border bg-white px-4 py-3 rounded-md">{error}</div>}

            <button type="submit" disabled={busy} className="btn-primary w-full">
              {busy ? "Confirming…" : "Confirm and sign in"}
            </button>

            <button type="button" onClick={onResend} className="btn-ghost w-full">
              Resend code
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
